import { createAsyncThunk } from "@reduxjs/toolkit";

import { toast } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import {
    GetAllAttendance, 
    GetAllUsersAttendance,
    GetAllEmployeeAttendance, 
    GetEmployeeAttendanceByDate, 
    GetEmployeeAttendanceByMonth,
    GetEmployeeAttendanceByDateRange,
    StartSalesDay, 
    CheckoutSalesDay, 
    GetSalesLogsByEmployee, 
    GetAllSalesData, 
    GetAllUsersWithSalesData,
    GetTravelPathData,
    GetUserRoutes,
    SalesVisitCheckIn, 
    SalesVisitCheckOut,
    CreateCheckIn,
    CreateCheckOut,
    GetTodaysAttendance,
    ApproveOvertime,
    GetPendingApprovals,
    GetApprovalStatistics as GetFaceApprovalStatistics,
    ApproveFaceRegistration,
    RejectFaceRegistration,
    DeleteFaceRegistration,
    SubmitFaceRegistration,
    GetLeavesForAttendance,
    GetPendingAttendanceApprovals,
    GetApprovedAttendanceApprovals,
    GetRejectedAttendanceApprovals,
    GetAttendanceApprovalStatistics,
    HandleApprovalAction,
    GetMyPendingApprovals,
    GetAttendanceInsights,
    GetMyCorrectionRequests,
    CreateCorrectionRequest,
    GetAllAttendanceApprovals,
} from "helpers/api_method";

export const getAllAttendances = createAsyncThunk("attendance/getAllAttendance", async (params: { month?: number; year?: number } = {}, { rejectWithValue }) => {
    try {
        const response = await GetAllAttendance(params?.month, params?.year);
        
        // Normalize API response: some layers may set numeric HTTP status on 'status'
        const payload = response as any;
        const hasBooleanStatus = typeof payload?.status === 'boolean';
        if (hasBooleanStatus && payload.status === false) {
            return rejectWithValue({ error: payload.message || 'Failed to fetch attendance data' });
        }
        return payload?.data ?? [];
    } catch (error: any) {
        // Handle timeout and other errors
        if (error === 'Request timeout. Please try again.' || error.message?.includes('timeout')) {
            return rejectWithValue({ error: 'Request timeout. Please try again.' });
        }
        return rejectWithValue({ error: error.message || 'Failed to fetch attendance data' });
    }
});

export const getAllUsersAttendances = createAsyncThunk("attendance/getAllUsersAttendance", async (params: { month?: number; year?: number } = {}, { rejectWithValue }) => {
    try {
        const response = await GetAllUsersAttendance(params?.month, params?.year);
        
        const payload = response as any;
        const hasBooleanStatus = typeof payload?.status === 'boolean';
        if (hasBooleanStatus && payload.status === false) {
            return rejectWithValue({ error: payload.message || 'Failed to fetch all users attendance data' });
        }
        return payload?.data ?? [];
    } catch (error: any) {
        // Handle timeout and other errors
        if (error === 'Request timeout. Please try again.' || error.message?.includes('timeout')) {
            return rejectWithValue({ error: 'Request timeout. Please try again.' });
        }
        return rejectWithValue({ error: error.message || 'Failed to fetch all users attendance data' });
    }
});

export const getAllEmployeeAttendance = createAsyncThunk("attendance/getAllEmployeeAttendance", async (event: any, { rejectWithValue }) => {
    try {
        const response = await GetAllEmployeeAttendance();
        
        const payload = response as any;
        const hasBooleanStatus = typeof payload?.status === 'boolean';
        if (hasBooleanStatus && payload.status === false) {
            return rejectWithValue({ error: payload.message || 'Failed to fetch employee attendance data' });
        }
        return payload?.data ?? [];
    } catch (error: any) {
        if (error === 'Request timeout. Please try again.' || error.message?.includes('timeout')) {
            return rejectWithValue({ error: 'Request timeout. Please try again.' });
        }
        return rejectWithValue({ error: error.message || 'Failed to fetch employee attendance data' });
    }
});

export const getEmployeeAttendanceByMonth = createAsyncThunk(
  "attendance/getEmployeeAttendanceByMonth",
  async ({ employee_id, month, year }: { employee_id: string; month: number; year: number }, { rejectWithValue }) => {
    try {
      const response = await GetEmployeeAttendanceByMonth(employee_id, month, year);
      
      const payload = response as any;
      const hasBooleanStatus = typeof payload?.status === 'boolean';
      if (hasBooleanStatus && payload.status === false) {
          return rejectWithValue({ error: payload.message || 'Failed to fetch employee attendance for month' });
      }
      return payload?.data ?? [];
    } catch (error: any) {
        if (error === 'Request timeout. Please try again.' || error.message?.includes('timeout')) {
            return rejectWithValue({ error: 'Request timeout. Please try again.' });
        }
        return rejectWithValue({ error: error.message || 'Failed to fetch employee attendance for month' });
    }
  }
);

export const getAttendanceInsights = createAsyncThunk(
  "attendance/getAttendanceInsights",
  async (_, { rejectWithValue }) => {
    try {
      const response = await GetAttendanceInsights();
      const payload = response as any;
      if (payload?.status === false) {
        return rejectWithValue({ error: payload.message || 'Failed to fetch insights' });
      }
      return payload?.data ?? null;
    } catch (error: any) {
      return rejectWithValue({ error: error.message || 'Failed to fetch insights' });
    }
  }
);

export const getMyCorrectionRequests = createAsyncThunk(
  "attendance/getMyCorrectionRequests",
  async (_, { rejectWithValue }) => {
    try {
      const response = await GetMyCorrectionRequests();
      const payload = response as any;
      if (payload?.status === false) {
        return rejectWithValue({ error: payload.message || 'Failed to fetch correction requests' });
      }
      return payload?.data ?? [];
    } catch (error: any) {
      return rejectWithValue({ error: error.message || 'Failed to fetch correction requests' });
    }
  }
);

export const createCorrectionRequest = createAsyncThunk(
  "attendance/createCorrectionRequest",
  async (data: { correction_type: string; reason: string; attendance_id?: string; requested_date?: string; requested_check_in?: string; requested_check_out?: string }, { rejectWithValue }) => {
    try {
      const response = await CreateCorrectionRequest(data);
      const payload = response as any;
      if (payload?.status === false) {
        return rejectWithValue({ error: payload.message || 'Failed to submit correction request' });
      }
      return payload?.data ?? null;
    } catch (error: any) {
      return rejectWithValue({ error: error.message || 'Failed to submit correction request' });
    }
  }
);

export const getEmployeeAttendanceByID = createAsyncThunk(
  "attendance/getEmployeeAttendanceByID",
  async ({ employee_id, date }: { employee_id: string; date: string }, { rejectWithValue }) => {
    try {
      const response = await GetEmployeeAttendanceByDate(employee_id, date);
      
      const payload = response as any;
      const hasBooleanStatus = typeof payload?.status === 'boolean';
      if (hasBooleanStatus && payload.status === false) {
          return rejectWithValue({ error: payload.message || 'Failed to fetch employee attendance details' });
      }
      return payload?.data ?? [];
    } catch (error: any) {
      if (error === 'Request timeout. Please try again.' || error.message?.includes('timeout')) {
        return rejectWithValue({ error: 'Request timeout. Please try again.' });
      }
      return rejectWithValue({ error: error.message || 'Failed to fetch employee attendance details' });
    }
  }
);

export const getEmployeeAttendanceByDateRange = createAsyncThunk(
  "attendance/getEmployeeAttendanceByDateRange",
  async (
    { employee_id, start_date, end_date }: { employee_id: string; start_date: string; end_date: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await GetEmployeeAttendanceByDateRange(employee_id, start_date, end_date);
      const payload = response as any;
      const hasBooleanStatus = typeof payload?.status === 'boolean';
      if (hasBooleanStatus && payload.status === false) {
        return rejectWithValue({ error: payload.message || 'Failed to fetch attendance for date range' });
      }
      return payload?.data ?? [];
    } catch (error: any) {
      if (error === 'Request timeout. Please try again.' || error.message?.includes('timeout')) {
        return rejectWithValue({ error: 'Request timeout. Please try again.' });
      }
      return rejectWithValue({ error: error.message || 'Failed to fetch attendance for date range' });
    }
  }
);

export const createCheckIn = createAsyncThunk("attendance/createCheckIn", async (event: any) => {
    try {
        const response = await CreateCheckIn(event);
        const apiResponse = response as any;
        if (apiResponse.status) {
            toast.success("Check-In Successful", { autoClose: 2000 });
        } else {
            toast.error(apiResponse.message || "Check-In Failed", { autoClose: 2000 });
        }
        return apiResponse;
    } catch (error) {
        toast.error("Check-In Failed", { autoClose: 2000 });
        return error;
    }
});

export const createCheckOut = createAsyncThunk("attendance/createCheckOut", async (event: any) => {
    try {
        const response = await CreateCheckOut(event);
        const apiResponse = response as any;
        if (apiResponse.status) {
            toast.success("Check-Out Successful", { autoClose: 2000 });
        } else {
            toast.error(apiResponse.message || "Check-Out Failed", { autoClose: 2000 });
        }
        return apiResponse;
    } catch (error) {
        toast.error("Check-Out Failed", { autoClose: 2000 });
        return error;
    }
});

export const getTodaysAttendance = createAsyncThunk("attendance/getTodaysAttendance", async () => {
    try {
        const response = await GetTodaysAttendance();
        return response as any;
    } catch (error) {
        return error;
    }
});

export const approveOvertime = createAsyncThunk("attendance/approveOvertime", async (event: any) => {
    try {
        const response = await ApproveOvertime(event.attendance_id, {
            action: event.action,
            notes: event.notes
        });
        if (response.data.status) {
            toast.success(`Overtime ${event.action} Successful`, { autoClose: 2000 });
        } else {
            toast.error(response.data.message || `Overtime ${event.action} Failed`, { autoClose: 2000 });
        }
        return response.data;
    } catch (error) {
        toast.error(`Overtime ${event.action} Failed`, { autoClose: 2000 });
        return error;
    }
});

// ------------------ SALES
export const getAllSalesAttendances = createAsyncThunk("attendance/getAllSalesAttendances", async () => {
    try {
        const response = await GetAllSalesData();
        return response.data;
    } catch (error) {
        return error;
    }
});

export const startSalesDay = createAsyncThunk("attendance/startSalesDay", async (event: any) => {
    try {
        const response = await StartSalesDay(event);
        // toast.success("Sales Day Started Successfully", { autoClose: 2000 });
        return response;
    } catch (error) {
        toast.error("Sales Day Start Failed", { autoClose: 2000 });
        return error;
    }
});
export const endSalesDay = createAsyncThunk("attendance/endSalesDay", async (event: any) => {
    try {
        const response = await CheckoutSalesDay(event);
        toast.success("Sales Day Completed Successfully", { autoClose: 2000 });
        return response.data;
    } catch (error) {
        toast.error("Sales Day Completion Failed", { autoClose: 2000 });
        return error;
    }
});

// Visit Locations
export const visitLocationCheckIn = createAsyncThunk("attendance/visitLocationCheckIn", async (event: any) => {
    try {
        const response = await SalesVisitCheckIn(event);
        // toast.success("Check-In Successful!", { autoClose: 2000 });
        return response;
    } catch (error) {
        toast.error("Check_in Failed", { autoClose: 2000 });
        return error;
    }
});
export const visitLocationCheckOut = createAsyncThunk("attendance/visitLocationCheckOut", async (event: any) => {
    try {
        const response = await SalesVisitCheckOut(event);
        toast.success("Check-Out Completed Successfully", { autoClose: 2000 });
        return response.data;
    } catch (error) {
        toast.error("Check-Out Failed", { autoClose: 2000 });
        return error;
    }
});

export const getSalesEntries = createAsyncThunk("attendance/getSalesEntries", async (event: any) => {
    try {
        const response = await GetSalesLogsByEmployee(event);
        return response.data;
    } catch (error) {
        return error;
    }
});

export const getAllUsersWithSalesData = createAsyncThunk(
    "attendance/getAllUsersWithSalesData",
    async (_, { rejectWithValue }) => {
        try {
            const response = await GetAllUsersWithSalesData();
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data);
        }
    }
);

export const getTravelPathData = createAsyncThunk(
    "attendance/getTravelPathData",
    async ({ userId, date }: { userId: string; date?: string }, { rejectWithValue }) => {
        try {
            const response = await GetTravelPathData(userId, date);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data);
        }
    }
);

export const getUserRoutes = createAsyncThunk(
    "attendance/getUserRoutes",
    async ({ userId, startDate, endDate }: { userId: string; startDate?: string; endDate?: string }, { rejectWithValue }) => {
        try {
            const response = await GetUserRoutes(userId, startDate, endDate);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data || { error: 'Failed to fetch user routes' });
        }
    }
);

// Facial Recognition Approval Thunks
export const getPendingApprovals = createAsyncThunk("attendance/getPendingApprovals", async (_, { rejectWithValue }) => {
    try {
        const response = await GetPendingApprovals();
        // The axios interceptor returns response.data, which contains the full API response
        // So response is already the { data: [...], message: "...", status: true } object
        return response;
    } catch (error: any) {
        console.error('getPendingApprovals error:', error);
        toast.error("Failed to fetch pending approvals", { autoClose: 2000 });
        return rejectWithValue(error.response?.data || { message: "Failed to fetch pending approvals" });
    }
});

export const getAttendanceApprovalStatistics = createAsyncThunk("attendance/getAttendanceApprovalStatistics", async (_, { rejectWithValue }) => {
    try {
        const response = await GetAttendanceApprovalStatistics();
        return response;
    } catch (error: any) {
        toast.error("Failed to fetch attendance approval statistics", { autoClose: 2000 });
        return rejectWithValue(error.response?.data || { message: "Failed to fetch attendance approval statistics" });
    }
});

export const approveFaceRegistration = createAsyncThunk("attendance/approveFaceRegistration", async (approvalId: string, { rejectWithValue }) => {
    try {
        const response = await ApproveFaceRegistration(approvalId);
        toast.success("Face registration approved successfully", { autoClose: 2000 });
        return response;
    } catch (error: any) {
        toast.error("Failed to approve face registration", { autoClose: 2000 });
        return rejectWithValue(error.response?.data || { message: "Failed to approve face registration" });
    }
});

export const rejectFaceRegistration = createAsyncThunk("attendance/rejectFaceRegistration", async ({ approvalId, reason }: { approvalId: string; reason: string }, { rejectWithValue }) => {
    try {
        const response = await RejectFaceRegistration(approvalId, reason);
        toast.success("Face registration rejected", { autoClose: 2000 });
        return response;
    } catch (error: any) {
        toast.error("Failed to reject face registration", { autoClose: 2000 });
        return rejectWithValue(error.response?.data || { message: "Failed to reject face registration" });
    }
});

export const submitFaceRegistration = createAsyncThunk("attendance/submitFaceRegistration", async (data: any, { rejectWithValue }) => {
    try {
        const response = await SubmitFaceRegistration(data);
        toast.success("Face registration submitted for approval", { autoClose: 2000 });
        return response;
    } catch (error: any) {
        toast.error("Failed to submit face registration", { autoClose: 2000 });
        return rejectWithValue(error.response?.data || { message: "Failed to submit face registration" });
    }
});

export const deleteFaceRegistration = createAsyncThunk("attendance/deleteFaceRegistration", async (approvalId: string, { rejectWithValue }) => {
    try {
        const response = await DeleteFaceRegistration(approvalId);
        toast.success("Face registration deleted successfully", { autoClose: 2000 });
        return response;
    } catch (error: any) {
        toast.error("Failed to delete face registration", { autoClose: 2000 });
        return rejectWithValue(error.response?.data || { message: "Failed to delete face registration" });
    }
});

export const getLeavesForAttendance = createAsyncThunk("attendance/getLeavesForAttendance", async (params: { month?: number; year?: number } = {}, { rejectWithValue }) => {
    try {
        const response = await GetLeavesForAttendance(params?.month, params?.year);
        
        const payload = response as any;
        const hasBooleanStatus = typeof payload?.status === 'boolean';
        if (hasBooleanStatus && payload.status === false) {
            return rejectWithValue({ error: payload.message || 'Failed to fetch leaves data' });
        }
        return payload?.data ?? [];
    } catch (error: any) {
        if (error === 'Request timeout. Please try again.' || error.message?.includes('timeout')) {
            return rejectWithValue({ error: 'Request timeout. Please try again.' });
        }
        return rejectWithValue({ error: error.message || 'Failed to fetch leaves data' });
    }
});

// Attendance Approval Thunks
export const getPendingAttendanceApprovals = createAsyncThunk("attendance/getPendingAttendanceApprovals", async (_, { rejectWithValue }) => {
    try {
        const response = await GetPendingAttendanceApprovals();
        return response;
    } catch (error: any) {
        console.error('getPendingAttendanceApprovals error:', error);
        toast.error("Failed to fetch pending attendance approvals", { autoClose: 2000 });
        return rejectWithValue(error.response?.data || { message: "Failed to fetch pending attendance approvals" });
    }
});

export const getApprovedAttendanceApprovals = createAsyncThunk("attendance/getApprovedAttendanceApprovals", async (_, { rejectWithValue }) => {
    try {
        const response = await GetApprovedAttendanceApprovals();
        return response;
    } catch (error: any) {
        console.error('getApprovedAttendanceApprovals error:', error);
        toast.error("Failed to fetch approved attendance approvals", { autoClose: 2000 });
        return rejectWithValue(error.response?.data || { message: "Failed to fetch approved attendance approvals" });
    }
});

export const getRejectedAttendanceApprovals = createAsyncThunk("attendance/getRejectedAttendanceApprovals", async (_, { rejectWithValue }) => {
    try {
        const response = await GetRejectedAttendanceApprovals();
        return response;
    } catch (error: any) {
        console.error('getRejectedAttendanceApprovals error:', error);
        toast.error("Failed to fetch rejected attendance approvals", { autoClose: 2000 });
        return rejectWithValue(error.response?.data || { message: "Failed to fetch rejected attendance approvals" });
    }
});

export const getApprovalStatistics = createAsyncThunk("attendance/getApprovalStatistics", async (_, { rejectWithValue }) => {
    try {
        const response = await GetFaceApprovalStatistics();
        return response;
    } catch (error: any) {
        toast.error("Failed to fetch approval statistics", { autoClose: 2000 });
        return rejectWithValue(error.response?.data || { message: "Failed to fetch approval statistics" });
    }
});

export const handleApprovalAction = createAsyncThunk("attendance/handleApprovalAction", async ({ approvalId, action, notes }: { approvalId: string; action: 'Approved' | 'Rejected'; notes?: string }, { rejectWithValue }) => {
    try {
        const response = await HandleApprovalAction(approvalId, { action, notes });
        toast.success(`Approval ${action.toLowerCase()} successfully`, { autoClose: 2000 });
        return response;
    } catch (error: any) {
        toast.error(`Failed to ${action.toLowerCase()} approval`, { autoClose: 2000 });
        return rejectWithValue(error.response?.data || { message: `Failed to ${action.toLowerCase()} approval` });
    }
});

export const getMyPendingApprovals = createAsyncThunk("attendance/getMyPendingApprovals", async (_, { rejectWithValue }) => {
    try {
        const response = await GetMyPendingApprovals();
        return response;
    } catch (error: any) {
        toast.error("Failed to fetch your pending approvals", { autoClose: 2000 });
        return rejectWithValue(error.response?.data || { message: "Failed to fetch your pending approvals" });
    }
});

// Combined approvals thunk – replaces 3 separate calls (approved + rejected + pending) with one
export const getAllAttendanceApprovals = createAsyncThunk(
    "attendance/getAllAttendanceApprovals",
    async (status: string = "", { rejectWithValue }) => {
        try {
            const response = await GetAllAttendanceApprovals(status || undefined);
            const payload = response as any;
            if (payload?.status === false) {
                return rejectWithValue({ error: payload.message || 'Failed to fetch approvals' });
            }
            return payload?.data ?? [];
        } catch (error: any) {
            return rejectWithValue(error.response?.data || { message: "Failed to fetch approvals" });
        }
    }
);














